'use client'

import * as React from "react"
import Link from "next/link"
import { Check, CaretUpDown, Star } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer"
import { Badge } from "@/components/ui/badge"
import { createPortal } from "react-dom"

import { ProviderIcon } from "@/components/ui/icons"
import { Model } from "@/lib/types"
import { models, ModelId } from "@/lib/models"
import { useUserContext } from "@/components/providers"
import { cn } from "@/lib/utils"
import { canUserAccessModel, FREE_MODEL_ID } from "@/lib/models/model-tiers"
import { ModalityBadges } from "@/components/ui/modality-badges"
import { PricingCore } from "@/lib/pricing/pricing-core"
import { calculateUsageFromCredits } from "@/lib/pricing/index"
import { formatFlexibleDate } from "@/lib/utils/date-utils"
import { toast } from "sonner"
import { Markdown } from "@/components/ui/markdown"
// import { profitMultiplier, profitMultiplierIfNotFree } from "@/lib/model-profit"
// import { SubscriptionComponent } from "@/components/subscription"


// Interface defining the props for the ModelSelector component
interface ModelSelectorProps {
    modelId: string                          // ID of the currently selected model
    onValueChange: (value: string) => void   // Callback function when a new model is selected
    disabled?: boolean                       // Flag to disable the selector
    className?: string                       // Additional CSS classes
    modelType: "text" | "image" | "video"    // Type of model (text or image)
    setPeekedModel: (model: Model) => void   // Function to set the currently hovered model
}

// Predefined categories for organizing models
const CATEGORIES: any = ["Favorites", "My agents", "Trending", "Free", "All", "Online search"]

// Image operation category labels
const IMAGE_OPERATION_CATEGORIES: Record<string, string> = {
    create: 'Image: Create',
    edit: 'Image: Edit',
    inpainting: 'Image: Inpainting',
    outpainting: 'Image: Outpainting',
    upscale: 'Image: Upscale',
    style_transfer: 'Image: Style Transfer'
}

// Component for rendering individual model type badges
const ModelTypeBadge: React.FC<{ type: string, isSelected: boolean, onClick: () => void }> = ({ type, isSelected, onClick }) => (
    <Badge
        variant={isSelected ? "outline" : "default"}
        className={cn(
            "mr-2 last:mr-0 hover:cursor-pointer",
            isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        )}
        onClick={onClick}
    >
        {type}
    </Badge>
)

// Simple cursor tooltip component
const CursorTooltip = ({ position, user }: { position: { x: number, y: number }, user: any }) => {
    const [isMounted, setIsMounted] = React.useState(false)

    React.useEffect(() => {
        setIsMounted(true)
        return () => setIsMounted(false)
    }, [])

    if (!isMounted) return null

    // Determine message based on user subscription
    let message = "Sign up to use this model"
    if (user) {
        if (user.subscription === 'free') {
            message = "Upgrade to access this model"
        } else if (user.subscription !== 'advanced' && user.subscription !== 'pro') {
            message = "Subscribe to access premium models"
        }
    }

    return createPortal(
        <div
            className="fixed bg-primary text-xs px-1.5 py-0.5 rounded text-primary-foreground z-[9999] pointer-events-none opacity-100"
            style={{
                left: `${position.x}px`,
                top: `${position.y + 15}px`,
                transform: 'translate(0, 0)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
        >
            {message}
        </div>,
        document.body
    )
}

// Main ModelSelector component
const ModelSelector: React.FC<ModelSelectorProps> = ({
    modelId,
    onValueChange,
    disabled = false,
    modelType,
    setPeekedModel,
}) => {
    // Add console log to debug
    // React.useEffect(() => {
    //     // console.log('ModelSelector onValueChange type:', typeof onValueChange)
    // }, [onValueChange])

    const { userContextValue, isUserAuthenticated } = useUserContext()
    const [availableModels, setAvailableModels] = React.useState<Record<string, Model[]>>({})
    const [filteredModels, setFilteredModels] = React.useState<Record<string, Model[]>>({})
    const [selectedModelType, setSelectedModelType] = React.useState<string>(modelType || "All")
    const [cursorPosition, setCursorPosition] = React.useState({ x: 0, y: 0 })
    const [showTooltip, setShowTooltip] = React.useState(false)
    const [searchQuery, setSearchQuery] = React.useState("")
    const isUserLoggedIn = React.useCallback(() => !!isUserAuthenticated, [isUserAuthenticated])

    // Track mouse position globally
    React.useEffect(() => {
        const trackMouse = (e: MouseEvent) => {
            if (showTooltip) {
                setCursorPosition({ x: e.clientX, y: e.clientY })
            }
        }

        window.addEventListener('mousemove', trackMouse)
        return () => window.removeEventListener('mousemove', trackMouse)
    }, [showTooltip])

    React.useEffect(() => {
        const baseCategories: string[] = [...CATEGORIES, ...Object.values(IMAGE_OPERATION_CATEGORIES)]
        const categories: string[] = isUserLoggedIn() ? baseCategories : ["Available to try without login", ...baseCategories]
        const categorizedModels: Record<string, Model[]> = categories.reduce((acc: Record<string, Model[]>, category: string) => {
            acc[category] = []
            return acc
        }, {})

        Object.values(models).forEach(model => {
            if (!model.active) return

            // Populate guest-available category when logged out
            if (!isUserLoggedIn()) {
                const accessibleForGuest =
                    canUserAccessModel(null, model) ||
                    FREE_MODEL_ID.includes(model.id as any) ||
                    (model as any)?.modelStyle?.free === true
                if (accessibleForGuest) {
                    categorizedModels["Available to try without login"].push(model)
                }
            }

            if (model.id.includes('assistant')) {
                categorizedModels['My agents'].push(model)
            } else {
                model.metadata.keywords.forEach(keyword => {
                    const category = keyword.charAt(0).toUpperCase() + keyword.slice(1)
                    if (categorizedModels[category]) {
                        categorizedModels[category].push(model)
                    }
                })
            }

            // Image operation categorization
            const outputsImage = model.architecture.outputModalities.includes('image' as any)
            const ops = (model.capabilities as any)?.imageOperations as string[] | undefined
            if (outputsImage && Array.isArray(ops)) {
                ops.forEach(op => {
                    const label = IMAGE_OPERATION_CATEGORIES[op]
                    if (label && categorizedModels[label]) {
                        categorizedModels[label].push(model)
                    }
                })
            }
            if (userContextValue?.settings.favoriteModels.includes(model.id)) {
                categorizedModels['Favorites'].push(model)
            }
            categorizedModels['All'].push(model)
        })

        // Hide guest category if empty
        if (!isUserLoggedIn() && categorizedModels["Available to try without login"].length === 0) {
            delete categorizedModels["Available to try without login"]
        }

        setAvailableModels(categorizedModels)
    }, [models, modelId, userContextValue?.settings, isUserLoggedIn])

    const modelTypes = React.useMemo(() => {
        const types = new Set<string>(["All"])
        Object.values(models).forEach(model => {
            model.architecture.outputModalities.forEach(type => types.add(type))
        })
        return Array.from(types)
    }, [models])

    // Do not force-sync to modelType prop; treat it as initial only so users can toggle badges

    React.useEffect(() => {
        const filtered: Record<string, Model[]> = {}
        Object.entries(availableModels).forEach(([category, modelList]) => {
            const filteredList = modelList.filter(model => {
                const matchesSelected = selectedModelType === "All" || model.architecture.outputModalities.includes(selectedModelType as any)
                return matchesSelected
            })

            filtered[category] = filteredList
        })
        setFilteredModels(filtered)
    }, [availableModels, selectedModelType, searchQuery, userContextValue?.settings, modelType])

    // Check if model is disabled based on authentication status and user permissions
    const isModelDisabled = (model: Model) => {
        return !canUserAccessModel(userContextValue, model)
    }

    return (
        <Command
            className="h-auto w-[full] p-1 font-sans"
            filter={(value, search) => {
                if (value.toLowerCase().includes(search.toLowerCase())) return 1
                return 0
            }}
        >
            <CommandInput placeholder="Search model..." />
            <div className="flex flex-row justify-start items-center p-2 border-b border-border overflow-auto scrollbar scrollbar-none">
                {modelTypes.map(type => (
                    <ModelTypeBadge
                        key={type}
                        type={type}
                        isSelected={selectedModelType === type}
                        onClick={() => setSelectedModelType(type)}
                    />
                ))}
            </div>
            <CommandList className="scrollbar scrollbar-track-transparent scrollbar-thumb-muted-foreground font-sans">
                <CommandEmpty>No models found.</CommandEmpty>
                {Object.entries(filteredModels)
                    .filter(([_, modelList]) => modelList.length > 0)
                    .map(([category, modelList], index) => {
                        // Sort models first by maker (alphabetically) and then by name (alphabetically)
                        const sortedModels = [...modelList].sort((a, b) => {
                            
                            if (a.maker !== b.maker) {
                                return a.maker.localeCompare(b.maker)
                            }
                            return a.name.localeCompare(b.name)
                        })

                        return (
                            <CommandGroup
                                key={category}
                                heading={
                                    category === 'All' && !isUserLoggedIn()
                                        ? (
                                            <div className="flex items-center justify-between w-full">
                                                {/* <span>{category.replace("Vision", "Vision (image compatible)")}</span> */}
                                                <Link href="/auth/sign-up" className="ml-0">
                                                    <span className="text-xs text-primary underline">Sign up for full access</span>
                                                    <span className="text-xs text-muted-foreground"> (free 500 credits)</span>
                                                </Link>
                                            </div>
                                        )
                                        : category.replace("Vision", "Vision (image compatible)")
                                }
                            >
                                {sortedModels.map((model, modelIndex) => {
                                    const modelDisabled = isModelDisabled(model)
                                    const key = `${category}-${model.id}-${model.name}-${model.maker}-${model.architecture.outputModalities}-${model.architecture.inputModalities}-${model.metadata.keywords}`
                                    return (
                                        <CommandItem
                                            key={key}
                                            value={key}
                                            onMouseEnter={(e) => {
                                                setPeekedModel(model)
                                                if (modelDisabled) {
                                                    setCursorPosition({
                                                        x: e.clientX,
                                                        y: e.clientY
                                                    })
                                                    setShowTooltip(true)
                                                }
                                            }}
                                            onMouseLeave={() => {
                                                if (modelDisabled) {
                                                    setShowTooltip(false)
                                                }
                                            }}
                                            onSelect={() => {
                                                // Only allow selection if user has permission to use the model
                                                if (modelDisabled) {
                                                    return
                                                }
                                                if (typeof onValueChange === 'function') {
                                                    onValueChange(model.id)
                                                } else {
                                                    console.error('Error: onValueChange is not a function', onValueChange)
                                                }
                                            }}
                                            className={cn(
                                                "aria-selected:bg-background4 hover:bg-accent hover:cursor-pointer rounded-md",
                                                "xs:py-3 sm:py-3 md:py-2", // Taller command items on mobile and tablet
                                                modelDisabled && "opacity-70" // Show as dimmed instead of completely disabled
                                            )}
                                        >
                                            <span className="inline-flex max-w-full items-center justify-between w-full">
                                                <span className="inline-flex items-center">
                                                    <ProviderIcon
                                                        maker={model.maker}
                                                        fallbackIcon={null}
                                                        className="h-3.5 w-3.5"
                                                    />
                                                    <span className="ml-1 truncate text-sm xs:text-sm">
                                                        {model.name}
                                                    </span>
                                                </span>
                                                {model.id === modelId ? <Check className={cn("h-4 w-4", model.id === modelId ? "opacity-100" : "opacity-0")} /> : null}
                                            </span>
                                        </CommandItem>
                                    )
                                })}
                            </CommandGroup>
                        )
                    })}
            </CommandList>

            {/* Portal-based tooltip that follows cursor */}
            {showTooltip && <CursorTooltip position={cursorPosition} user={userContextValue} />}
        </Command>
    )
}




// Component for displaying detailed information about a hovered model
// Updated to match the new design (Variation3) and refined styling
const ModelSelectorHoverCard: React.FC<{ peekedModel: Model, className?: string }> = ({ peekedModel, className = '' }) => {
    const { userContextValue, isUserAuthenticated, setUserContextValue } = useUserContext()

    const isFavorite = userContextValue?.settings.favoriteModels.includes(peekedModel.id)

    const handleModelFavorites = async (model: Model) => {
        if (!isUserAuthenticated) {
            toast.error('Please log in to favorite models')
            return
        }

        const newFavorites = isFavorite
            ? userContextValue.settings.favoriteModels.filter(id => id !== model.id)
            : [...userContextValue.settings.favoriteModels, model.id]

        try {
            await setUserContextValue('favoriteModels', newFavorites, true)
        } catch (error) {
            toast.error('Failed to update favorites')
            console.error('Error updating favorites:', error)
        }
    }

    // Calculate estimated usage for 1000 credits
    const userBalance = userContextValue?.balance ? userContextValue.balance * 1000 : 500
    const estimatedUsage = calculateUsageFromCredits(peekedModel.id as ModelId, userBalance)

    return (
        <div className={cn(
            "w-[286px] bg-popover border border-border rounded-lg overflow-y-auto shadow-lg font-sans scrollbar scrollbar-thumb-muted-foreground/50 scrollbar-track-transparent",
            className
        )}>
            {/* Header - Use popover background */}
            <div className="bg-popover p-4 flex items-center justify-between border-b border-border">
                <div className="flex items-center gap-3 min-w-0"> {/* Increased gap slightly */}
                    <ProviderIcon maker={peekedModel.maker} fallbackIcon={null} className="h-6 w-6 flex-shrink-0" />
                    <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground truncate">{peekedModel.maker}</p> {/* text-xs, font-medium */}
                        <h3 className="font-semibold text-sm truncate">{peekedModel.name}</h3> {/* text-sm, font-semibold */}
                    </div>
                </div>
                <button 
                    className="text-muted-foreground hover:text-amber-400 transition-colors flex-shrink-0 ml-2"
                    onClick={() => {toast.info('clicked' + peekedModel.id); handleModelFavorites(peekedModel)}}
                > 
                    <Star className={cn("h-5 w-5", isFavorite ? "fill-amber-400 text-amber-400" : "")} />
                </button>
            </div>

            {/* Content - Use popover background */}
            <div className="p-4 space-y-4 bg-popover">
                {/* Metadata: Release Date & Estimated Usage */}
                <div className="flex justify-between text-sm">
                    <div className="flex flex-col">
                        <p className="text-xs font-medium text-muted-foreground">Released</p>
                        <p className="text-sm">{formatFlexibleDate(peekedModel.metadata.releaseDate) || 'N/A'}</p>
                    </div>
                    <div className="flex flex-col items-end">
                        <div className="text-xs font-medium text-muted-foreground inline-flex items-center"> 
                            Est. Usage ({userBalance < 1000 ? Math.floor(userBalance) : Math.floor(userBalance / 1000) + 'k'} credits)
                        </div>
                        <p className="text-sm">
                            {estimatedUsage
                                ? estimatedUsage.unit === 'unknown'
                                    ? 'N/A'
                                    : estimatedUsage.count === Infinity
                                        ? 'Unlimited'
                                        : `~${estimatedUsage.count.toLocaleString()} ${estimatedUsage.unit}`
                                : 'N/A'}
                        </p>
                    </div>
                </div>

                {/* Capabilities (Modalities) */}
                <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Capabilities</p>
                    <ModalityBadges types={[...peekedModel.architecture.inputModalities, ...peekedModel.architecture.outputModalities]} />
                </div>

                {/* Context & Knowledge Cutoff */}
                <div className="text-sm space-y-2 pt-3 border-t border-border/50">
                    <div>
                        <p className="text-xs font-medium text-muted-foreground">Context Window</p>
                        <p className="text-sm">{peekedModel.architecture.contextLength ? peekedModel.architecture.contextLength.toLocaleString() : 'N/A'} tokens</p>
                    </div>
                </div>

                {/* Description */}
                <div className="pt-3 border-t border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                    <Markdown className="text-sm text-foreground/90">
                        {peekedModel.metadata.description}
                    </Markdown>
                </div>
            </div>
        </div>
    )
}





interface ModelSelectorContainerProps {
    model: Model                           // The currently selected model
    onModelChange: (model: Model) => void   // Callback function when a new model is selected
    disabled?: boolean                       // Flag to disable the selector
    modelType: 'text' | 'image' | 'video'    // Type of model
}

export const ModelSelectorContainer: React.FC<ModelSelectorContainerProps> = ({
    model,
    onModelChange,
    disabled = false,
    modelType,
}) => {
    const [peekedModel, setPeekedModel] = React.useState<Model | undefined>(model || undefined)
    const [isInnerDrawerOpen, setIsInnerDrawerOpen] = React.useState(false)
    const { userContextValue, isUserAuthenticated } = useUserContext()

    // Get model ID from the model
    const modelId = model?.id || ''

    // Add fallback for when model doesn't exist
    const currentModel = React.useMemo(() => {
        return model || Object.values(models)[0]
    }, [model])

    // Handle model selection
    const handleModelSelection = (selectedModelId: string) => {
        // Only allow selecting models the user has access to
        if (!canUserAccessModel(userContextValue, models[selectedModelId as ModelId])) {
            toast.error('You do not have access to this model')
            return
        }


        if (typeof onModelChange === 'function' && models[selectedModelId as ModelId]) {
            onModelChange(models[selectedModelId as ModelId])
        } else {
            console.error('Error: onModelChange is not a function or model not found', onModelChange, selectedModelId)
        }
    }

    // If guest and current model isn't accessible, switch to a free model (but don't clobber valid selections)
    React.useEffect(() => {
        if (!isUserAuthenticated) {
            const currentModel = models[modelId as ModelId]
            const currentIsAccessible = currentModel ? canUserAccessModel(null, currentModel) : false
            if (!currentIsAccessible) {
                const firstFreeId = Array.isArray(FREE_MODEL_ID) ? FREE_MODEL_ID[0] : undefined
                if (firstFreeId && models[firstFreeId as ModelId] && modelId !== firstFreeId) {
                    onModelChange(models[firstFreeId as ModelId])
                }
            }
        }
    }, [isUserAuthenticated, modelId, onModelChange])

    const triggerContent = (
        <div className="h-8 xs:w-auto sm:w-[200px] flex items-center justify-between">
            <div className="flex flex-row items-center gap-2 text-sm overflow-hidden min-w-0 max-w-[calc(100%-24px)]">
                <ProviderIcon maker={currentModel?.maker || ''} fallbackIcon={null} className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{currentModel?.name || 'Select a model'}</span>
            </div>
            <div className="w-6 flex-shrink-0 flex justify-center">
                <CaretUpDown className="h-4 w-4 opacity-50" />
            </div>
        </div>
    )

    const [isModelSelectorOpen, setIsModelSelectorOpen] = React.useState(false)

    return (
        <>
            {/* Desktop version - Popover (no extra wrapper background) */}
            <div className="hidden md:block">
                <Popover open={isModelSelectorOpen} onOpenChange={setIsModelSelectorOpen}>
                    <PopoverTrigger className={cn(
                        "w-auto h-8 flex items-center justify-between border border-border rounded-md px-3 font-medium transition-all duration-200 ease-in-out",
                        disabled && "cursor-not-allowed",
                        isModelSelectorOpen && "border-ring"
                    )}>
                        {triggerContent}
                    </PopoverTrigger>
                    <PopoverContent className="p-0">
                        <ModelSelector
                            modelId={modelId}
                            onValueChange={handleModelSelection}
                            modelType={modelType}
                            disabled={disabled}
                            setPeekedModel={setPeekedModel}
                        />
                        {peekedModel && (
                            <ModelSelectorHoverCard className="absolute top-0 left-[294px] max-h-96" peekedModel={peekedModel} />
                        )}
                    </PopoverContent>
                </Popover>
            </div>

            {/* Mobile version - Drawer */}
            <div className="block md:hidden">
                <Drawer>
                    <DrawerTrigger className={cn(
                        "w-auto h-8 flex items-center justify-between border border-border rounded-md px-3 font-medium",
                        disabled && "cursor-not-allowed"
                    )}>
                        {triggerContent}
                    </DrawerTrigger>
                    <DrawerContent className="flex flex-col gap-3 py-3 bg-popover font-sans">
                        <DrawerTitle className="px-3">Model Selector</DrawerTitle>
                        <ModelSelector
                            modelId={modelId}
                            onValueChange={handleModelSelection}
                            modelType={modelType}
                            disabled={disabled}
                            setPeekedModel={setPeekedModel}
                        />
                        <div className="flex flex-row w-full items-center gap-3 px-3">
                            <Drawer open={isInnerDrawerOpen} onOpenChange={setIsInnerDrawerOpen}>
                                <DrawerTrigger asChild>
                                    <Button
                                        className="flex h-10 w-1/2 items-center justify-center rounded-md border border-border bg-accent text-foreground hover:bg-accent/50"
                                        onClick={() => setIsInnerDrawerOpen(true)}
                                    >
                                        Model information
                                    </Button>
                                </DrawerTrigger>
                                <DrawerContent className="bg-popover h-5/6 font-sans">
                                    <DrawerTitle className="sr-only">Model information</DrawerTitle>
                                    <div className="h-full flex flex-col justify-between">
                                        {currentModel && (
                                            <ModelSelectorHoverCard
                                                className="rounded-md shadow-none w-full h-full border-0"
                                                peekedModel={currentModel}
                                            />
                                        )}
                                        <div className="p-4">
                                            <Button
                                                className="w-full"
                                                onClick={() => setIsInnerDrawerOpen(false)}
                                            >
                                                OK
                                            </Button>
                                        </div>
                                    </div>
                                </DrawerContent>
                            </Drawer>
                            <DrawerClose className="w-1/2 h-10 border border-border rounded-md bg-primary text-background">
                                Use {currentModel?.name || 'selected model'}
                            </DrawerClose>
                        </div>
                    </DrawerContent>
                </Drawer>
            </div>
        </>
    )
}